import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "taskguard-ui/button";
import { EmptyState } from "taskguard-ui/empty-state";
import { TextInput } from "taskguard-ui/inputs";
import { SectionHeading } from "taskguard-ui/section-heading";
import {
  BoardPanel,
  Composer,
  ComposerRow,
  ComposerShell,
  TodoActions,
  TodoContent,
  TodoList,
  TodoRow,
  TodoTextButton,
  TodoToggle,
} from "taskguard-ui/surfaces";
import { CopyText, Eyebrow } from "taskguard-ui/text";
import { TODO_SCHEMA_VERSION, toVisibleTodos } from "../shared/todos";
import { formatDateTime, parseDateTimeLocalValue, toDateTimeLocalValue } from "../format";
import { useTodoStore } from "../store";

const todoMetaStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.35rem 0.9rem",
  marginTop: "0.4rem",
};

const editorStackStyle: CSSProperties = {
  display: "grid",
  gap: "0.7rem",
};

const inlineFieldStyle: CSSProperties = {
  display: "grid",
  gap: "0.35rem",
  flex: "1 1 240px",
  minWidth: "min(100%, 240px)",
};

const dueEditorRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.65rem",
  alignItems: "end",
};

const composerDetailsStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.75rem",
  alignItems: "end",
  marginTop: "0.75rem",
};

const boardTitleStyle: CSSProperties = {
  fontSize: "2.45rem",
  fontWeight: 700,
  lineHeight: 1.05,
};

const boardCountStyle: CSSProperties = {
  margin: "20px 0 0 6px",
  color: "#b8c0ca",
  fontSize: "1.05rem",
  fontWeight: 800,
};

const boardMenuButtonStyle: CSSProperties = {
  position: "absolute",
  top: "85px",
  right: "11px",
  width: "24px",
  height: "18px",
  display: "inline-grid",
  placeItems: "center",
  padding: 0,
  border: 0,
  background: "transparent",
  color: "#ffffff",
  cursor: "pointer",
  fontSize: "1.15rem",
  fontWeight: 800,
  lineHeight: 1,
};

const todoMenuButtonStyle: CSSProperties = {
  width: "24px",
  height: "24px",
  display: "inline-grid",
  placeItems: "center",
  padding: 0,
  border: 0,
  background: "transparent",
  color: "#ffffff",
  cursor: "pointer",
  fontSize: "1.15rem",
  fontWeight: 800,
  lineHeight: 1,
};

const addTaskButtonStyle: CSSProperties = {
  width: "100%",
  minHeight: "45px",
  marginTop: "15px",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 46px",
  alignItems: "stretch",
  padding: 0,
  border: 0,
  borderRadius: "4px",
  overflow: "hidden",
  background: "#020810",
  color: "#ffffff",
  cursor: "pointer",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.34)",
};

const addTaskLabelStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.72rem",
  fontSize: "1rem",
  fontWeight: 800,
};

const addTaskChevronStyle: CSSProperties = {
  display: "inline-grid",
  placeItems: "center",
  borderLeft: "1px solid rgba(255, 255, 255, 0.08)",
  fontSize: "1.7rem",
  fontWeight: 600,
  lineHeight: 1,
};

const addTaskPlusStyle: CSSProperties = {
  fontSize: "1.55rem",
  fontWeight: 500,
  lineHeight: 1,
};

type TodoItemProps = {
  id: string;
  text: string;
  completed: boolean;
  createdAt: number;
  updatedAt: number;
  dueAt: number | null;
};

function TodoItem({ id, text, completed, createdAt, updatedAt, dueAt }: TodoItemProps) {
  const toggleTodo = useTodoStore((state) => state.toggleTodo);
  const updateTodo = useTodoStore((state) => state.updateTodo);
  const removeTodo = useTodoStore((state) => state.removeTodo);
  const [draftText, setDraftText] = useState(text);
  const [draftDueAt, setDraftDueAt] = useState(toDateTimeLocalValue(dueAt));
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraftText(text);
  }, [text]);

  useEffect(() => {
    setDraftDueAt(toDateTimeLocalValue(dueAt));
  }, [dueAt]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const resetDrafts = () => {
    setDraftText(text);
    setDraftDueAt(toDateTimeLocalValue(dueAt));
  };

  const save = () => {
    updateTodo(id, {
      text: draftText,
      dueAt: parseDateTimeLocalValue(draftDueAt),
    });
    setIsEditing(false);
  };

  const cancel = () => {
    resetDrafts();
    setIsEditing(false);
  };

  return (
    <TodoRow completed={completed}>
      <TodoToggle checked={completed} onChange={() => toggleTodo(id)} />

      <TodoContent>
        {isEditing ? (
          <div style={editorStackStyle}>
            <TextInput
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraftText(event.currentTarget.value)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") {
                  save();
                }

                if (event.key === "Escape") {
                  cancel();
                }
              }}
              ref={inputRef}
              value={draftText}
            />

            <div style={dueEditorRowStyle}>
              <div style={inlineFieldStyle}>
                <label htmlFor={`todo-due-${id}`}>
                  <Eyebrow as="span">Due date</Eyebrow>
                </label>
                <TextInput
                  id={`todo-due-${id}`}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraftDueAt(event.currentTarget.value)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "Enter") {
                      save();
                    }

                    if (event.key === "Escape") {
                      cancel();
                    }
                  }}
                  type="datetime-local"
                  value={draftDueAt}
                />
              </div>

              {draftDueAt.length > 0 ? (
                <Button onClick={() => setDraftDueAt("")} size="subtle" type="button" variant="ghost">
                  Clear due date
                </Button>
              ) : null}
            </div>

            <CopyText as="div" style={todoMetaStyle}>
              <span>Created {formatDateTime(createdAt)}</span>
              <span>Updated {formatDateTime(updatedAt)}</span>
            </CopyText>
          </div>
        ) : (
          <div>
            <TodoTextButton completed={completed} onClick={() => setIsEditing(true)}>
              {text}
            </TodoTextButton>
          </div>
        )}
      </TodoContent>

      <TodoActions>
        {isEditing ? (
          <>
            <Button onClick={save} size="subtle" type="button">
              Save
            </Button>
            <Button onClick={cancel} size="subtle" type="button" variant="ghost">
              Cancel
            </Button>
          </>
        ) : (
          <button aria-label={`Edit ${text}`} onClick={() => setIsEditing(true)} style={todoMenuButtonStyle} type="button">
            ...
          </button>
        )}
        {isEditing ? (
          <Button onClick={() => removeTodo(id)} size="subtle" tone="danger" type="button" variant="ghost">
            Delete
          </Button>
        ) : null}
      </TodoActions>
    </TodoRow>
  );
}

export function TodoBoard() {
  const navigate = useNavigate();
  const activeVault = useTodoStore((state) => (state.activeVaultId == null ? null : (state.vaults[state.activeVaultId] ?? null)));
  const addTodo = useTodoStore((state) => state.addTodo);
  const todos = useMemo(
    () =>
      activeVault == null
        ? []
        : toVisibleTodos({
            schemaVersion: TODO_SCHEMA_VERSION,
            deviceId: "",
            lastMutatedAt: activeVault.lastMutatedAt,
            todos: activeVault.todos,
          }),
    [activeVault],
  );
  const openCount = todos.filter((todo) => !todo.completed).length;
  const [draft, setDraft] = useState("");
  const [draftDueAt, setDraftDueAt] = useState("");
  const [isComposing, setIsComposing] = useState(false);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (draft.trim().length === 0) {
      return;
    }

    addTodo(draft, parseDateTimeLocalValue(draftDueAt));
    setDraft("");
    setDraftDueAt("");
    setIsComposing(false);
  };

  if (activeVault == null) {
    return (
      <BoardPanel empty>
        <SectionHeading eyebrowText="Tasks" title="Pick a vault before you start." />
        <EmptyState title="No active vault selected.">
          Create a vault or join one from the vaults view, then your task list will appear here.
        </EmptyState>
        <Button block onClick={() => navigate("/vaults")} type="button">
          Open vaults
        </Button>
      </BoardPanel>
    );
  }

  return (
    <BoardPanel>
      <h1 style={boardTitleStyle}>Inbox</h1>
      <div style={boardCountStyle}>{openCount}</div>
      <button aria-label="Inbox actions" style={boardMenuButtonStyle} type="button">
        ...
      </button>

      {todos.length === 0 ? (
        <EmptyState title="Your list is clear.">Add the first task and it will appear anywhere this vault is connected.</EmptyState>
      ) : (
        <TodoList>
          {todos.map((todo) => (
            <TodoItem
              completed={todo.completed}
              createdAt={todo.createdAt}
              dueAt={todo.dueAt}
              id={todo.id}
              key={todo.id}
              text={todo.text}
              updatedAt={todo.updatedAt}
            />
          ))}
        </TodoList>
      )}

      {isComposing ? (
        <Composer onSubmit={handleSubmit}>
          <ComposerShell>
            <label htmlFor="todo-input">
              <Eyebrow as="span">Add a task</Eyebrow>
            </label>
            <ComposerRow>
              <TextInput
                autoComplete="off"
                autoFocus
                id="todo-input"
                name="todo"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraft(event.currentTarget.value)}
                placeholder="Add a task..."
                spellCheck={false}
                value={draft}
              />
              <Button type="submit">Add</Button>
            </ComposerRow>

            <div style={composerDetailsStyle}>
              <div style={inlineFieldStyle}>
                <label htmlFor="todo-due-input">
                  <Eyebrow as="span">Due date</Eyebrow>
                </label>
                <TextInput
                  id="todo-due-input"
                  name="todoDueAt"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraftDueAt(event.currentTarget.value)}
                  type="datetime-local"
                  value={draftDueAt}
                />
              </div>

              <Button onClick={() => setIsComposing(false)} size="subtle" type="button" variant="ghost">
                Close
              </Button>
              {draftDueAt.length > 0 ? (
                <Button onClick={() => setDraftDueAt("")} size="subtle" type="button" variant="ghost">
                  Clear due date
                </Button>
              ) : null}
            </div>
          </ComposerShell>
        </Composer>
      ) : (
        <button onClick={() => setIsComposing(true)} style={addTaskButtonStyle} type="button">
          <span style={addTaskLabelStyle}>
            <span aria-hidden="true" style={addTaskPlusStyle}>
              +
            </span>
            Add task
          </span>
          <span aria-hidden="true" style={addTaskChevronStyle}>
            ⌃
          </span>
        </button>
      )}
    </BoardPanel>
  );
}
